// 초기 스탯 v4. 서버 InitialStatModel.java 를 그대로 옮긴 것이다.
// 수치·임계값은 고서의 수치가 아닌 게임 환산 초안이며, 완전한 격국/용신/신강약 판정이 아니다.
import { STEM_EL, GROUPS, SIPSEONG, mod } from './calc.js';
import { analyzeCombinations } from './natal.js';

export const VERSION = 'initial-stats-v4';
export const MAX_INITIAL = 45;
const SHARE_FULL = 0.60;
const USAGE_WEIGHT = 0.05;

const HIDDEN_STEMS = [[9], [5, 9, 7], [0, 2, 4], [1], [4, 1, 9], [2, 4, 6], [3, 5], [5, 3, 1], [6, 8, 4], [7], [4, 7, 3], [8, 0]];
const HIDDEN_GAME_SHARES = [[], [1], [0.70, 0.30], [0.60, 0.25, 0.15]];
const STEM_POSITION = [0.90, 1.00, 0, 0.90];
const BRANCH_POSITION = [1.00, 1.35, 1.15, 1.00];
const MONTH_RELATION = [1.20, 1.08, 0.82, 0.88, 0.95];
const LIMITS = [
  '일간 지원지수는 게임용 강약 추정이며 고서의 신강약 완전판정이 아닙니다.',
  '천간합·완성 방합·삼합·육합은 제한적으로 반영하며, 합화·충형해의 전체 해법·종격·격국 성패·용신·사령 분일은 미판정입니다.',
  '합의 보정 계수와 중복 제외는 게임 환산 규칙이며, 고서의 정량 공식이 아닙니다.',
  '암합은 사용자 결정에 따라 초기 능력치 계산에서 제외합니다.',
  '조후는 갑목 인월의 화·진월의 수 조건만 일부 반영하며 나머지는 미판정입니다.',
];
const ROOT = { NONE: ['무근', 0], STORAGE: ['묘고', 0.35], RESIDUAL: ['여기·부기', 0.45], GROWTH: ['장생지의 지장간', 0.85], MAIN: ['본기', 1.00] };

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const round = (v) => Math.round(v * 10000) / 10000;

function rootType(stem, branch) {
  const element = STEM_EL[stem];
  const hidden = HIDDEN_STEMS[branch];
  if (STEM_EL[hidden[0]] === element) return 'MAIN';
  if (!hidden.some((h) => STEM_EL[h] === element)) return 'NONE';
  const growth = [11, 2, -1, 5, 8], storage = [7, 10, -1, 1, 4];
  if (stem % 2 === 0 && branch === growth[element]) return 'GROWTH';
  if (branch === storage[element]) return 'STORAGE';
  return 'RESIDUAL';
}
function roots(stem, pillars) {
  const found = [];
  for (let pos = 0; pos < pillars.length; pos++) {
    if (!pillars[pos]) continue;
    const type = rootType(stem, pillars[pos].branch);
    if (type !== 'NONE') found.push({ position: pos, branch: pillars[pos].branch, type, label: ROOT[type][0] });
  }
  return found;
}
function strongestRoot(stem, pillars) {
  let best = 0;
  for (const r of roots(stem, pillars)) best = Math.max(best, ROOT[r.type][1] * (r.position === 1 ? 1.15 : 1));
  return best;
}
function monthFactor(stem, monthBranch) {
  const monthElement = STEM_EL[HIDDEN_STEMS[monthBranch][0]];
  return MONTH_RELATION[mod(STEM_EL[stem] - monthElement, 5)];
}
function add(ten, elements, dayStem, stem, force) {
  const relation = mod(STEM_EL[stem] - STEM_EL[dayStem], 5);
  ten[relation * 2 + (stem % 2 === dayStem % 2 ? 0 : 1)] += force;
  elements[STEM_EL[stem]] += force;
}
function climate(dayStem, monthBranch, elements) {
  if (dayStem !== 0 || (monthBranch !== 2 && monthBranch !== 4)) return { scope: '미판정(갑목 인월·진월 외 조후)', factor: 1 };
  const early = monthBranch === 2;
  const needed = elements[early ? 1 : 4], contrary = elements[early ? 4 : 1];
  const factor = 0.94 + 0.08 * needed / (needed + 1) - 0.04 * Math.max(0, contrary - needed) / (contrary + 1);
  return { scope: early ? '갑목 인월: 초봄 화의 온난 조건 일부 적용' : '갑목 진월: 늦봄 수의 윤택 조건 일부 적용', factor };
}

export function analyze(s, requestedFloor) {
  const dayStem = s.day.stem, monthBranch = s.month.branch;
  const pillars = [s.year, s.month, s.day, s.hour];
  const ten = new Array(10).fill(0), elementForces = new Array(5).fill(0);
  const dayRoots = roots(dayStem, pillars);
  const combinations = analyzeCombinations(s);

  for (let pos = 0; pos < pillars.length; pos++) {
    const pillar = pillars[pos];
    if (!pillar) continue; // 모르는 시주는 채우거나 4주 분량으로 재정규화하지 않는다.
    if (pos !== 2) {
      const rooted = strongestRoot(pillar.stem, pillars);
      const force = STEM_POSITION[pos] * (0.80 + 0.30 * rooted) * monthFactor(pillar.stem, monthBranch) * combinations.stems[pos];
      add(ten, elementForces, dayStem, pillar.stem, force);
    }
    const hidden = HIDDEN_STEMS[pillar.branch];
    const shares = HIDDEN_GAME_SHARES[hidden.length];
    for (let i = 0; i < hidden.length; i++) {
      const force = BRANCH_POSITION[pos] * shares[i] * monthFactor(hidden[i], monthBranch) * combinations.branches[pos][STEM_EL[hidden[i]]];
      add(ten, elementForces, dayStem, hidden[i], force);
    }
  }

  const groups = [0, 0, 0, 0, 0];
  for (let i = 0; i < ten.length; i++) groups[Math.trunc(i / 2)] += ten[i];
  const total = groups.reduce((a, b) => a + b, 0);
  const self = monthFactor(dayStem, monthBranch) * (0.80 + 0.60 * strongestRoot(dayStem, pillars));
  const support = (self + groups[0] + groups[4]) / (self + total);
  const capacity = clamp((support - 0.15) / 0.65, 0, 1);
  const usage = [clamp(1.10 - 0.45 * support, 0.60, 1), 0.50 + 0.50 * capacity, 0.35 + 0.65 * capacity, 0.35 + 0.65 * capacity, clamp(1.12 - 0.50 * support, 0.60, 1)];
  for (let group = 1; group <= 3; group++) {
    const concentration = clamp((groups[group] / total - 0.45) / 0.55, 0, 1);
    const shortage = clamp((0.40 - support) / 0.40, 0, 1);
    usage[group] *= 1 - 0.40 * concentration * shortage;
  }
  const cl = climate(dayStem, monthBranch, elementForces);
  const floor = Math.max(5, Math.min(MAX_INITIAL, requestedFloor));
  const stats = [0, 0, 0, 0, 0], natalConditions = [0, 0, 0, 0, 0], uses = {};
  for (let i = 0; i < groups.length; i++) {
    const presence = groups[i] / (groups[i] + 1.75);
    natalConditions[i] = presence * usage[i];
    const share = total > 0 ? groups[i] / total : 0;
    const scaled = clamp(share / SHARE_FULL, 0, 1) * (1 - USAGE_WEIGHT * (1 - usage[i]));
    stats[i] = Math.round(floor + (MAX_INITIAL - floor) * scaled);
    stats[i] = Math.max(floor, Math.min(MAX_INITIAL, stats[i]));
    uses[GROUPS[i]] = round(usage[i]);
  }
  const forces = {};
  for (let i = 0; i < ten.length; i++) forces[SIPSEONG[i]] = round(ten[i]);
  let meanCondition = 0;
  for (const v of natalConditions) meanCondition += v / natalConditions.length;
  const affinity = {};
  for (let i = 0; i < natalConditions.length; i++) {
    const relative = meanCondition > 0 ? natalConditions[i] / meanCondition : 1;
    affinity[GROUPS[i]] = round(clamp(1 + 0.25 * (relative - 1), 0.85, 1.15));
  }
  const limits = [...LIMITS];
  if (!s.hour) limits.push('시주 모름: 알려진 세 기둥만 사용한 잠정 출발값입니다.');
  const strengthEstimate = support < 0.40 ? '지원 적음(게임 추정)' : support > 0.65 ? '지원 많음(게임 추정)' : '지원 중간(게임 추정)';
  return {
    stats, tenGodForces: forces, usageFactors: uses, daySupportIndex: round(support), strengthEstimate,
    dayRoots, climateScope: cl.scope, climateFactor: round(cl.factor), limits, eventAffinity: affinity,
    combinations: combinations.evidence,
  };
}
